import { ForeignDataTreeView } from '@/Pages/Graph/src/components/debug/common/foreign-data-tree';
import {
  foreignDataToTree,
  parseForeignData,
} from '@/Pages/Graph/src/data/foreign-data';
import { RunDetailFunction } from '@/Pages/Run/components/RunDetailsFunctions';
import { CodeData } from '@/components/Code/Code';
import { CodeLanguageType } from '@/components/Code/types';
import { Message } from '@/components/Message';
import { TextWithLinks } from '@/components/TextWithLinks';
import { Text } from '@/design-system/';
import {
  DatasetSchema,
  ExampleSchema,
  MessageUnionType,
  RunSchema,
} from '@/types/schema';
import { isOpenAIFunctions } from '@/utils/ai-tools';
import {
  getMessageContent,
  getMessageFields,
  getMessageType,
} from '@/utils/messages';
import { cn } from '@/utils/tailwind';

import { ExperimentLanguage } from '../types';

export function DatasetSessionCompareSidePaneDetailInputs(props: {
  inputs: ExampleSchema['inputs'] | RunSchema['inputs'] | undefined;
  dataType: DatasetSchema['data_type'];
  height?: string;
  language?: ExperimentLanguage;
}) {
  let inputElement: React.ReactElement | null = null;
  if (props.language === 'pretty' && props.dataType === 'kv') {
    inputElement = (
      <ForeignDataTreeView
        data={foreignDataToTree({
          data: parseForeignData(props.inputs),
        })}
      />
    );
  } else {
    inputElement = (
      <CodeData
        value={props.inputs}
        readOnly
        omitMaxHeight
        variant="plain"
        language={
          ['json', 'yaml'].includes(props.language ?? '')
            ? (props.language as CodeLanguageType)
            : 'json'
        }
        fontSize="14px"
      />
    );

    if (props.dataType === 'llm') {
      inputElement = (
        <div
          className={cn('rounded-lg border border-secondary')}
          data-testid="input-detail-code-element-llm"
        >
          <TextWithLinks text={props.inputs?.input ?? ''} />
        </div>
      );
    }

    if (props.dataType === 'chat') {
      const inputMessages = props.inputs?.input;

      inputElement = inputMessages ? (
        <div className="flex flex-col gap-4">
          {isOpenAIFunctions(props.inputs?.functions) && (
            <div className="overflow-hidden rounded-lg border border-secondary">
              {props.inputs?.functions.map((fn, idx) => (
                <RunDetailFunction key={idx} function={fn} />
              ))}
            </div>
          )}
          {inputMessages?.map((message: MessageUnionType, index: number) => {
            return (
              <Message
                key={index}
                role={getMessageType(message) ?? 'HUMAN'}
                content={getMessageContent(message) ?? ''}
                fields={getMessageFields(message)}
                omitMaxHeight
              />
            );
          })}
        </div>
      ) : (
        inputElement
      );
    }
  }

  if (
    !props.inputs ||
    (typeof props.inputs === 'object' && Object.keys(props.inputs).length === 0)
  ) {
    return <Text className="text-disabled">No input</Text>;
  }

  return (
    <div
      className="scrollbar-track-transparent h-full overflow-auto"
      style={{ height: props.height }}
    >
      {inputElement}
    </div>
  );
}
